import React from 'react';
import { useIntl } from 'react-intl';
import { Alert, Input } from 'reactstrap';
import type { PublishTiming } from 'fym-shared/src/music24/oneOffPublish';
import type { SongPublishSharing } from 'fym-shared/src/music24/songPublish';
import { TypedMemo } from '../../../common/types';
import { PublishTimingValue } from '../../promote/publish/PublishTimingField';
import { soundCloudUploadMessages as m } from '../soundCloudMessages';
import { SongUploadChoice } from './SongUploadChoice';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for SongUpload; each part uses its own slice of it */
import styles from './SongUpload.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongUploadWhenProps {
  timings: PublishTiming[];
  value: PublishTimingValue;
  sharing: SongPublishSharing;
  releaseDate: string | null;
  onChange: (value: PublishTimingValue) => void;
}

/** "When does it go up?" (design c3, c4) and the line that SoundCloud has no scheduled release. */
const SongUploadWhenComponent: React.FC<SongUploadWhenProps> = ({ timings, value, sharing, releaseDate, onChange }) => {
  const intl = useIntl();
  const day =
    releaseDate === null
      ? ''
      : intl.formatDate(`${releaseDate}T00:00:00`, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
  const titles: Record<PublishTiming, string> = {
    now: intl.formatMessage(m.whenNow),
    releaseDay: intl.formatMessage(m.whenReleaseDay, { date: day }),
    later: intl.formatMessage(m.whenLater),
  };
  const hints: Record<PublishTiming, string> = {
    now: intl.formatMessage(sharing === 'private' ? m.whenNowPrivate : m.whenNowPublic),
    releaseDay: intl.formatMessage(m.whenReleaseDayHint, { date: day }),
    later: intl.formatMessage(m.whenLaterHint),
  };
  return (
    <>
      <fieldset className={styles.box}>
        <legend className={styles.label}>{intl.formatMessage(m.when)}</legend>
        <div className={styles.options}>
          {timings.map((timing) => (
            <SongUploadChoice
              key={timing}
              name="song-upload-when"
              value={timing}
              checked={value.timing === timing}
              title={titles[timing]}
              hint={hints[timing]}
              onSelect={() => onChange({ ...value, timing })}
            />
          ))}
        </div>
        {value.timing === 'later' && (
          <Input
            type="datetime-local"
            aria-label={intl.formatMessage(m.whenPick)}
            value={value.pickedAt}
            onChange={(event) => onChange({ timing: 'later', pickedAt: event.target.value })}
          />
        )}
      </fieldset>
      {value.timing !== 'now' && (
        <Alert color="info" className="mb-0 small">
          {withBrandWord(intl.formatMessage(m.noSchedule))}
        </Alert>
      )}
    </>
  );
};

export const SongUploadWhen = TypedMemo(SongUploadWhenComponent);
